import Link from "next/link";
import React, { useState } from "react";
import Image from "next/image";
import {
  FacebookCircleIcon,
  InstagramCircleIcon,
  LinkedinCircleIcon,
  TikTokCircleIcon,
} from "../Icon";
import Overlay from "../atomic/Overlay";
import BugReport from "../BugReport";

import GoogleMap from "@/components/GoogleMaps";

function Footer() {
  const [showBugReport, setShowBugReport] = useState(false);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="mt-12 md:mt-0 relative w-full overflow-hidden bg-[#0A2540] text-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.06]">
        <svg
          className="h-full w-full"
          viewBox="0 0 1440 700"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1440 520C1180 360 980 360 720 520C460 680 260 680 0 520"
            stroke="white"
            strokeWidth="1"
          />
          <path
            d="M1440 420C1180 260 980 260 720 420C460 580 260 580 0 420"
            stroke="white"
            strokeWidth="1"
          />
          <path
            d="M1440 320C1180 160 980 160 720 320C460 480 260 480 0 320"
            stroke="white"
            strokeWidth="1"
          />
        </svg>
      </div>

      <div className="relative z-10 w-full px-5 md:px-12 lg:px-20 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center">
          {/* LEFT */}
          <div>
            {/* Logo */}
            <Link href="/" className="inline-block">
              <Image
                src="/img/logo/ClampHook_Logo_Primary.png"
                alt="ClampHook"
                width={240}
                height={60}
                loading="lazy"
                className="h-auto w-[180px] md:w-[230px] object-contain brightness-0 invert"
              />
            </Link>

            {/* Description */}
            <p className="max-w-xl text-[15px] md:text-base leading-8 text-white/72 font-light">
              The best IOE entrance preparation institute situated in Kathmandu,
              Nepal along with CSIT entrance preparation.
            </p>

            {/* Navigation */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-10">
              <div>
                <h2 className="mb-5 text-xs font-semibold tracking-[0.18em] uppercase text-white/95">
                  Programs
                </h2>

                <div className="flex flex-col gap-3">
                  <Link
                    href="/ioe-entrance-preparation"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    IOE Entrance Preparation
                  </Link>

                  <Link
                    href="/ioe-mock-test"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Free IOE Mock Test
                  </Link>

                  <Link
                    href="/classrooms"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    All Courses
                  </Link>

                  <Link
                    href="/syllabus"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Syllabus
                  </Link>
                </div>
              </div>

              <div>
                <h2 className="mb-5 text-xs font-semibold tracking-[0.18em] uppercase text-white/95">
                  Platform
                </h2>

                <div className="flex flex-col gap-3">
                  <Link
                    href="/aboutus"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    About
                  </Link>

                  <Link
                    href="/team"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Team
                  </Link>

                  <Link
                    href="/contact"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Contact
                  </Link>

                  <button
                    onClick={() => setShowBugReport(true)}
                    className="text-left text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Report Bug
                  </button>
                </div>
              </div>

              <div>
                <h2 className="mb-5 text-xs font-semibold tracking-[0.18em] uppercase text-white/95">
                  Resources
                </h2>

                <div className="flex flex-col gap-3">
                  <Link
                    href="/blog"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Blog
                  </Link>

                  <Link
                    href="/faqs"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    FAQs
                  </Link>

                  <Link
                    href="/terms?tab=privacyPolicy"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>

                  <Link
                    href="/terms?tab=termsAndConditions"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Terms & Conditions
                  </Link>

                  <Link
                    href="/terms?tab=cookiesPolicy"
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    Cookies Policy
                  </Link>
                </div>
              </div>

              <div>
                <h2 className="mb-5 text-xs font-semibold tracking-[0.18em] uppercase text-white/95">
                  Payments
                </h2>

                <div className="flex flex-col gap-5">
                  <div className="relative h-6 w-28">
                    <Image
                      src="/img/esewa-white.png"
                      fill
                      sizes="112px"
                      alt="Esewa"
                      className="object-contain object-left"
                    />
                  </div>
                  <div className="relative h-6 w-28">
                    <Image
                      src="/img/khalti-white.png"
                      fill
                      sizes="112px"
                      alt="Khalti"
                      className="object-contain object-left brightness-110"
                    />
                  </div>

                  <div className="relative h-6 w-28">
                    <Image
                      src="/img/logo/external/fone_pay.png"
                      fill
                      sizes="112px"
                      alt="Fonepay"
                      className="object-contain object-left brightness-110"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="mt-14 flex items-center gap-4">
              <a
                href="https://www.facebook.com/clamphookioe"
                target="_blank"
                rel="noreferrer"
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                <FacebookCircleIcon />
              </a>

              <a
                href="https://www.linkedin.com/company/clamphookioe/"
                target="_blank"
                rel="noreferrer"
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                <LinkedinCircleIcon />
              </a>

              <a
                href="https://www.instagram.com/clamphook"
                target="_blank"
                rel="noreferrer"
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                <InstagramCircleIcon />
              </a>

              <a
                href="https://www.tiktok.com/@clamphook_"
                target="_blank"
                rel="noreferrer"
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                <TikTokCircleIcon />
              </a>
            </div>
          </div>

          {/* RIGHT MAP */}
          <div className="w-full mt-10 lg:mt-0">
            <div className="overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.03]">
              <GoogleMap />
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 md:mt-20 border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex flex-col">
            <p className="text-xs md:text-sm text-white/40">
              © {new Date().getFullYear()} Clamphook. All rights reserved.
            </p>

            <p className="text-xs text-white/30">
              Improved & maintained by{" "}
              <a
                href="https://shahnishant.com.np"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-white/70 transition-colors"
              >
                Nishant Shah
              </a>
            </p>
          </div>

          <div className="flex items-center gap-6">
            <Link
              href="/terms?tab=privacyPolicy"
              className="text-xs md:text-sm text-white/50 hover:text-white transition-colors"
            >
              Privacy
            </Link>

            <Link
              href="/terms?tab=termsAndConditions"
              className="text-xs md:text-sm text-white/50 hover:text-white transition-colors"
            >
              Terms
            </Link>

            <Link
              href="/contact"
              className="text-xs md:text-sm font-semibold text-white/90 hover:text-white transition-colors underline underline-offset-2 decoration-white/30 hover:decoration-white"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Bug Report */}
      <Overlay show={showBugReport} onHide={() => setShowBugReport(false)}>
        <BugReport onHide={() => setShowBugReport(false)} />
      </Overlay>
    </footer>
  );
}

export default Footer;
